import {
  BANK_PROVIDER_LABELS,
  BANK_PROVIDERS,
  resolveBankingId
} from './bankingSchema';
import { isMockBankingRecord } from './bankingMockUtils';
import { isOwnBankAccountPartyName, isOwnCompanyIban } from './ownBankAccounts';
import { toJsDate } from './bankingDateTime';

const OUR_ACCOUNT_HOLDER = 'Mehmet Demirsoy';

const IBAN_REGEX = /TR\d{2}(?:\s?\d){22}/gi;
const BANK_CORE = [
  'Kuveyt Türk',
  'Türkiye Finans',
  'Ziraat',
  'VakıfBank',
  'Vakıf(?:bank)?',
  'Garanti(?: BBVA)?',
  'Yapı(?: ve)? Kredi',
  'QNB(?: Finansbank)?',
  'Finansbank',
  'Denizbank',
  'Türk Ekonomi',
  'TEB',
  'Halkbank',
  'Halk',
  'Albaraka(?: Türk)?',
  'Şekerbank',
  'İş',
  'ING',
  'HSBC',
  'Fibabanka',
  'Odeabank',
  'Anadolubank',
  'Burgan',
  'Akbank',
  'Enpara(?:\\.com)?'
].join('|');
const BANK_NAME_REGEX = new RegExp(
  `(?:T\\.?C\\.? )?(?:Türkiye )?(?:${BANK_CORE})(?: Katılım)?(?: Bankası)?(?: A\\.?[ŞS]\\.?| Anonim Şirketi)?`,
  'i'
);

const stripNoisePhrases = (s) =>
  String(s || '')
    .replace(/\([^)]*\)/g, ' ')
    .replace(/elden teslim (al[ıi]nan|edilen) k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/k[ıi]ymetli maden (al[ıi]m|sat[ıi][şs]) bedeli/gi, ' ')
    .replace(/tcmb fast [üu]creti[\s\S]*?tutar[ıi](n[ıi]n)?/gi, ' ')
    .replace(/\bfast [öo]demesi\b/gi, ' ')
    .replace(/\b(virman|referans|nolu|no'lu|numaral[ıi]|sorgu|tarihli)\b/gi, ' ')
    // Banka ünvanı artıkları: "Bankası A.Ş.", "Katılım Bankası", "T.A.Ş."
    .replace(/(^|\s)(bankas[ıi]|bank|kat[ıi]l[ıi]m)(?=\s|$)/gi, ' ')
    .replace(/\bT?\.?\s*A\.?\s*[ŞS]\.?(?=\s|$)/g, ' ')
    .replace(/\bAnonim [ŞS]irketi\b/gi, ' ')
    .replace(/\b(giden|gelen)\b/gi, ' ')
    .replace(/\b\d[\d./:-]{4,}\b/g, ' ');

const isIbanLike = (value) => /^TR\d{2}/i.test(String(value || '').replace(/\s/g, ''));

const looksLikePersonName = (value) => {
  const s = String(value || '').trim();
  if (!s || isIbanLike(s)) return false;
  if (/^\d+$/.test(s.replace(/\s/g, ''))) return false;
  const letters = s.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, '');
  return letters.length >= 4;
};

/**
 * Taraf adından banka ünvanı artıklarını temizler.
 *
 * VakıfBank bazı FAST kayıtlarında gönderen alanını "Bankası A.Ş. MEHMET
 * DEMİRSOY" gibi banka ünvanıyla birlikte yazıyor (19.10.2026 Kavaklı). Ünvan
 * atıldıktan sonra geriye en az iki kelimelik bir isim kalıyorsa temizlenmiş
 * hâli kullanılır; aksi hâlde kayıt olduğu gibi bırakılır.
 */
export const stripBankTitle = (value) => {
  const ham = String(value || '').trim();
  if (!ham) return '';
  const temiz = ham
    .replace(BANK_NAME_REGEX, ' ')
    .replace(/(^|\s)(bankas[ıi]|bank|kat[ıi]l[ıi]m)(?=\s|$)/gi, ' ')
    .replace(/\bT?\.?\s*A\.?\s*[ŞS]\.?(?=\s|$)/g, ' ')
    .replace(/\bAnonim [ŞS]irketi\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[-–:,()[\]{}\s.]+|[-–:,()[\]{}\s.]+$/g, '')
    .trim();
  if (temiz === ham) return ham;
  const kelimeler = temiz.split(' ').filter(Boolean);
  if (kelimeler.length < 2) return ham;
  return looksLikePersonName(temiz) ? temiz : ham;
};

const sanitizePartyName = (value) => {
  const s = stripBankTitle(String(value || '').trim());
  return looksLikePersonName(s) ? s : '';
};

const namesEqual = (a, b) =>
  String(a || '').trim().toLocaleLowerCase('tr-TR') === String(b || '').trim().toLocaleLowerCase('tr-TR');

const pickRaw = (rawData, keys) => {
  if (!rawData || typeof rawData !== 'object') return '';
  for (const k of keys) {
    const v = rawData[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
};

const normText = (value) => String(value ?? '').trim();

const BUSINESS_WORDS = new Set([
  'teslim',
  'elden',
  'kıymetli',
  'kiymetli',
  'maden',
  'alım',
  'alim',
  'alı',
  'ali',
  'satış',
  'satis',
  'bedeli',
  'bedel',
  'ödeme',
  'odeme',
  'virman',
  'havale',
  'eft',
  'fast',
  'mobil',
  'mobıl',
  'atm',
  'çekme',
  'cekme',
  'kodu',
  'kod',
  'şube',
  'sube',
  'hesap',
  'tutar',
  'referans',
  'cep',
  'hvl'
]);

const hasBusinessWord = (value) => {
  const lower = String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[^a-zçğıöşü0-9\s]/g, ' ');
  return lower
    .split(/\s+/)
    .filter(Boolean)
    .some((w) => BUSINESS_WORDS.has(w));
};

const isLikelyPersonName = (value) => {
  const s = normText(value);
  if (!s || s.length < 5 || s.length > 90) return false;
  if (/^[\s.\-_/|,]+$/.test(s)) return false;
  if (/^TR\d{2}/i.test(s.replace(/\s/g, ''))) return false;
  if (/^\d+$/.test(s.replace(/\s/g, ''))) return false;
  if (/^[A-Z0-9._-]{6,}$/i.test(s) && !/\s/.test(s)) return false;
  const lower = s.toLocaleLowerCase('tr-TR');
  if (/kodu\s*:/.test(lower) || /\batm\b/.test(lower)) return false;
  if (hasBusinessWord(s)) return false;
  const letters = s.replace(/[^A-Za-zÇĞİÖŞÜçğıöşü]/g, '');
  if (letters.length < 4) return false;
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length < 2 || words.length > 5) return false;
  return words.every((w) => /[A-Za-zÇĞİÖŞÜçğıöşü]{2,}/.test(w) && !/\d{3,}/.test(w));
};

const CORR_NAME_KEYS = new Set([
  'corrnamesurnametext',
  'corrname',
  'corrtitle',
  'corrunvan',
  'correspondentname',
  'counterpartynametext',
  'counterpartyname',
  'beneficiaryname',
  'beneficiarynamesurname',
  'karsiad',
  'karsiunvan',
  'aliciadi',
  'aliciunvan',
  'receivername'
]);

const OWN_NAME_KEYS = new Set(['namesurnametext', 'accountholdername']);
const REMARK_KEYS = new Set(['remark', 'detailtext', 'narrative', 'remittanceinfo', 'paymentpurpose']);

/**
 * Garanti explanation yapısal isim: FAST7187228611-ÇİĞDEM DUYMAZ-
 * Banka sıkça corrNameSurnameText=null bırakır; isim yalnızca explanation'dadır.
 */
const extractStructuredNameFromExplanation = (explanation, ownName) => {
  const text = normText(explanation);
  if (!text) return '';

  const tryName = (raw) => {
    const name = normText(String(raw || '').replace(/^[-–:,\s]+|[-–:,\s]+$/g, ''));
    if (!isLikelyPersonName(name)) return '';
    if (ownName && namesEqual(name, ownName)) return '';
    return name;
  };

  const fastDash = text.match(/\bFAST\d+-([^-_\n]{3,80}?)-/i);
  if (fastDash) {
    const hit = tryName(fastDash[1]);
    if (hit) return hit;
  }
  const fastEnd = text.match(/\bFAST\d+-([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü. ]{2,70})\s*$/i);
  if (fastEnd) {
    const hit = tryName(fastEnd[1]);
    if (hit) return hit;
  }
  const intHvl = text.match(/\bINT-HVL-+\s*-?\s*([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü. ]{2,70})/i);
  if (intHvl) {
    const hit = tryName(intHvl[1]);
    if (hit) return hit;
  }
  const hvlTail = text.match(/\bHVL-+(?:[^-]*-)*([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü. ]{2,70})\s*$/i);
  if (hvlTail) {
    const hit = tryName(hvlTail[1]);
    if (hit) return hit;
  }
  return '';
};

const extractCounterpartyFromExplanation = (explanation, ownName) => {
  const structured = extractStructuredNameFromExplanation(explanation, ownName);
  if (structured) return structured;

  let text = normText(explanation);
  if (!text) return '';

  const lower = text.toLocaleLowerCase('tr-TR');
  const noisePatterns = [
    /^(cep\s*şube|mobil|mobıl|internet|şube)[-:\s]*/i,
    /\bfast\d+/gi,
    /\bhvl[-:\s]*/gi,
    /\bint[- ]?hvl[-:\s]*/gi,
    /\([^)]*\)/g,
    /elden\s+teslim[\s\S]{0,100}?maden(?:\s+(?:alım\w*|alim\w*|satış\w*|satis\w*|alı|ali|bedel\w*))*/gi,
    /\bkıymetli\s+maden(?:\s+(?:alım\w*|alim\w*|satış\w*|satis\w*|alı|ali|bedel\w*))*/gi,
    /\bkiymetli\s+maden(?:\s+(?:alım\w*|alim\w*|satış\w*|satis\w*|alı|ali|bedel\w*))*/gi,
    /\b(virman|referans|nolu|ödeme|bedeli|tutarı|alınan|edilen|teslim)\b/gi
  ];

  let cleanedLower = lower;
  for (const re of noisePatterns) {
    cleanedLower = cleanedLower.replace(re, ' ');
  }
  cleanedLower = cleanedLower.replace(/[-–_/|,]+/g, ' ').replace(/\s+/g, ' ').trim();

  const wordsLower = cleanedLower.split(/\s+/).filter(Boolean);
  if (!wordsLower.length) return '';

  const originalTokens = text
    .split(/[-–_/|,.\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const mapToOriginalCasing = (lowerWords) =>
    lowerWords
      .map((lw) => {
        const hit = originalTokens.find((ow) => ow.toLocaleLowerCase('tr-TR') === lw);
        return hit || lw;
      })
      .join(' ');

  for (let n = Math.min(4, wordsLower.length); n >= 2; n -= 1) {
    const candidate = mapToOriginalCasing(wordsLower.slice(-n));
    if (isLikelyPersonName(candidate) && !namesEqual(candidate, ownName)) return candidate;
  }
  return '';
};

/**
 * Garanti rawData.enrichmentInformation — gerçek API + legacy.
 * Giden FAST'te isim MUS dışında veya explanation'da olabilir.
 */
export const extractGarantiEnrichmentFromRaw = (raw) => {
  const enrichment = Array.isArray(raw?.enrichmentInformation) ? raw.enrichmentInformation : [];
  const customerName = normText(raw?.customerName);
  let corrName = '';
  let ownName = '';
  let corrIban = '';
  let corrAccount = '';

  const applyMus = (mus) => {
    if (!mus || typeof mus !== 'object') return;
    const corrExplicit = normText(mus.corrNameSurnameText || mus.corrName || mus.corrTitle || mus.corrUnvan);
    if (corrExplicit && isLikelyPersonName(corrExplicit)) corrName = corrName || corrExplicit;

    const nameSurname = normText(mus.nameSurnameText);
    if (nameSurname && isLikelyPersonName(nameSurname)) {
      if (customerName && namesEqual(nameSurname, customerName)) {
        ownName = ownName || nameSurname;
      } else if (!normText(mus.corrNameSurnameText || mus.corrName)) {
        corrName = corrName || nameSurname;
      } else {
        ownName = ownName || nameSurname;
      }
    }

    corrIban = corrIban || normText(mus.corrIBAN || mus.corrIban);
    const unit = normText(mus.corrUnitNum);
    const acct = normText(mus.corrAccountNum);
    if (unit || acct) corrAccount = corrAccount || [unit, acct].filter(Boolean).join('/');
  };

  const applyCorrAcct = (val) => {
    if (!val || typeof val !== 'object') return;
    corrIban = corrIban || normText(val.corrIBAN || val.corrIban);
    const unit = normText(val.corrUnitNum);
    const acct = normText(val.corrAccountNum);
    if (unit || acct) corrAccount = corrAccount || [unit, acct].filter(Boolean).join('/');
    const nestedName = normText(
      val.corrNameSurnameText || val.corrName || val.corrTitle || val.accountName || val.corrAccountName
    );
    if (nestedName && isLikelyPersonName(nestedName)) corrName = corrName || nestedName;
  };

  const scanObject = (obj, depth = 0) => {
    if (!obj || typeof obj !== 'object' || depth > 4) return;
    for (const [rawKey, value] of Object.entries(obj)) {
      const key = String(rawKey || '').trim().toLowerCase();
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        scanObject(value, depth + 1);
        continue;
      }
      const text = normText(value);
      if (!isLikelyPersonName(text)) continue;
      if (customerName && namesEqual(text, customerName)) {
        ownName = ownName || text;
        continue;
      }
      if (CORR_NAME_KEYS.has(key) || key.includes('corrname') || key.includes('beneficiary')) {
        corrName = corrName || text;
        continue;
      }
      if (OWN_NAME_KEYS.has(key)) {
        if (customerName && namesEqual(text, customerName)) {
          ownName = ownName || text;
        }
        continue;
      }
      if (REMARK_KEYS.has(key)) corrName = corrName || text;
    }
  };

  for (const info of enrichment) {
    if (!info || typeof info !== 'object') continue;
    const code = normText(info.enrichmentCode || info.moduleName).toUpperCase();
    const value = info.enrichmentValue && typeof info.enrichmentValue === 'object' ? info.enrichmentValue : null;
    if (code === 'MUS' && value) applyMus(value);
    if ((code === 'CORRACNT' || code === 'CORRACCT') && value) applyCorrAcct(value);
    if (info.MUS || info.mus) applyMus(info.MUS || info.mus);
    if (info.CORRACNT || info.corracnt || info.CORRACCT) {
      applyCorrAcct(info.CORRACNT || info.corracnt || info.CORRACCT);
    }
    if (value && code && code !== 'MUS' && code !== 'CORRACNT' && code !== 'CORRACCT') {
      scanObject(value);
    }
    if (info.EFT || info.eft) scanObject(info.EFT || info.eft);
    if (info.DEK || info.dek) scanObject(info.DEK || info.dek);
  }

  if (!ownName) ownName = customerName;
  if (corrName && ownName && namesEqual(corrName, ownName)) corrName = '';
  if (corrName && customerName && namesEqual(corrName, customerName)) corrName = '';

  if (!corrName) {
    corrName = extractCounterpartyFromExplanation(
      raw?.explanation || raw?.description || '',
      ownName || customerName
    );
  }

  return { corrName, ownName, corrIban, corrAccount };
};

const parseMovementParties = (description) => {
  const text = String(description || '').replace(/\s+/g, ' ').trim();
  const empty = { senderName: '', receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
  if (!text) return empty;

  const cleanSpaces = (s) => String(s || '').replace(/\s+/g, ' ').trim();
  const firstIban = (s) => {
    const m = String(s || '').match(IBAN_REGEX);
    return m && m[0] ? cleanSpaces(m[0]) : '';
  };
  const stripIbans = (s) => String(s || '').replace(IBAN_REGEX, ' ');
  const nameClean = (raw) => {
    const cleaned = cleanSpaces(stripNoisePhrases(stripIbans(raw)))
      .replace(/^[-–:,()[\]{}\s]+|[-–:,()[\]{}\s]+$/g, '')
      .trim();
    const words = cleaned.split(' ').filter(Boolean);
    return words.length > 4 ? words.slice(-4).join(' ') : cleaned;
  };

  const fastMatch = text.match(/FAST\d+-([^-]+?)-/i);
  if (fastMatch) {
    const person = nameClean(fastMatch[1]);
    if (looksLikePersonName(person)) {
      return { senderName: person, receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
    }
  }

  const intHvlMatch = text.match(/INT-HVL-+\s*-?\s*([A-ZÇĞİÖŞÜ][A-ZÇĞİÖŞÜa-zçğıöşü\s.]{2,})/i);
  if (intHvlMatch) {
    const person = nameClean(intHvlMatch[1]);
    if (looksLikePersonName(person)) {
      return { senderName: person, receiverName: '', bankName: '', senderIban: '', receiverIban: '' };
    }
  }

  const fromMatch = text.match(/hesab[ıi]ndan/i);
  const toMatch = text.match(/hesab[ıi]na/i);

  let senderRaw = '';
  let receiverRaw = '';
  if (fromMatch) {
    senderRaw = text.slice(0, fromMatch.index);
    if (toMatch && toMatch.index > fromMatch.index) {
      receiverRaw = text.slice(fromMatch.index + fromMatch[0].length, toMatch.index);
    }
  } else {
    const rawMatches = text.match(IBAN_REGEX) || [];
    if (rawMatches[0]) {
      const i0 = text.indexOf(rawMatches[0]);
      senderRaw = text.slice(0, i0);
      if (rawMatches[1]) {
        const i1 = text.indexOf(rawMatches[1], i0 + rawMatches[0].length);
        receiverRaw = text.slice(i0 + rawMatches[0].length, i1);
      }
    }
  }

  const senderIban = firstIban(senderRaw);
  const receiverIban = firstIban(receiverRaw);
  const bankMatch = (receiverRaw && receiverRaw.match(BANK_NAME_REGEX)) || text.match(BANK_NAME_REGEX);
  const bankName = bankMatch ? cleanSpaces(bankMatch[0]) : '';

  // Banka ünvanı ADA KARIŞMAZ — iki taraftan da temizlenir. VakıfBank
  // açıklaması "... Türkiye Garanti Bankası A.Ş. MEHMET DEMİRSOY hesabından
  // ..." biçiminde geliyor; eskiden yalnız alıcı tarafı temizlendiği için
  // gönderen "Bankası A.Ş. MEHMET DEMİRSOY" diye yazılıyordu (19.10.2026).
  const senderName = nameClean(String(senderRaw).replace(BANK_NAME_REGEX, ' '));
  const receiverName = nameClean(String(receiverRaw).replace(BANK_NAME_REGEX, ' '));

  return { senderName, receiverName, bankName, senderIban, receiverIban };
};

export const normalizeBankMovement = (m, provider, bankingId) => {
  const isGaranti = provider === BANK_PROVIDERS.GARANTI;
  const bank = isGaranti ? BANK_PROVIDERS.GARANTI : (m.bank || m.provider || provider);
  const bankLabel = isGaranti
    ? BANK_PROVIDER_LABELS[BANK_PROVIDERS.GARANTI]
    : (m.bankLabel || m.bankName || BANK_PROVIDER_LABELS[bank] || bank);
  return {
    ...m,
    bankingId: m.bankingId || bankingId,
    bank,
    bankLabel,
    bankLogoKey: m.bankLogoKey || bank,
    provider: isGaranti ? BANK_PROVIDERS.GARANTI : (m.provider || bank),
    bankName: isGaranti ? BANK_PROVIDER_LABELS[BANK_PROVIDERS.GARANTI] : (m.bankName || bankLabel)
  };
};

export const movementRowKey = (row) => `${row?.provider || row?.bank || 'bank'}-${row?.id || ''}`;

export const mergeBranchBankMovements = (vakifRows = [], garantiRows = [], bankingId) => {
  const id = bankingId || '';
  const tagged = [
    ...vakifRows.map((m) => normalizeBankMovement(m, BANK_PROVIDERS.VAKIFBANK, id)),
    ...garantiRows.map((m) => normalizeBankMovement(m, BANK_PROVIDERS.GARANTI, id))
  ].filter((m) => !isMockBankingRecord(m));

  const seen = new Set();
  const deduped = tagged.filter((m) => {
    const key = `${m.provider || m.bank}|${m.bankingId}|${m.externalTxId || m.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return deduped.sort((a, b) => {
    const ta = toJsDate(a?.bookingDate || a?.valueDate)?.getTime() || 0;
    const tb = toJsDate(b?.bookingDate || b?.valueDate)?.getTime() || 0;
    if (tb !== ta) return tb - ta;
    return String(b.id || '').localeCompare(String(a.id || ''));
  });
};

export const matchesBankProviderFilter = (row, filter) => {
  if (!filter || filter === 'all') return true;
  const bank = String(row?.bank || '').trim().toLowerCase();
  const prov = String(row?.provider || '').trim().toLowerCase();
  const target = String(filter || '').trim().toLowerCase();
  return bank === target || prov === target;
};

export const resolveMovementDetail = (movement) => {
  const raw = movement?.rawData || {};
  const parsed = parseMovementParties(movement?.description);
  const direction = movement?.direction;
  const enriched = extractGarantiEnrichmentFromRaw({
    ...raw,
    explanation: raw.explanation || movement?.description || '',
    customerName: raw.customerName || movement?.customerName
  });
  const parsedPerson =
    sanitizePartyName(parsed.senderName) || sanitizePartyName(parsed.receiverName) || '';

  let senderName =
    sanitizePartyName(movement?.senderName) ||
    sanitizePartyName(pickRaw(raw, ['gonderenAdi', 'gonderenAd', 'senderName', 'gonderen'])) ||
    '';
  let receiverName =
    sanitizePartyName(movement?.receiverName) ||
    sanitizePartyName(pickRaw(raw, ['aliciAdi', 'aliciAd', 'receiverName', 'alici', 'karsiUnvan', 'karsiAd'])) ||
    '';

  if (direction === 'out') {
    senderName = sanitizePartyName(enriched.ownName) || OUR_ACCOUNT_HOLDER;
    const receiverCandidate =
      receiverName ||
      sanitizePartyName(enriched.corrName) ||
      sanitizePartyName(parsed.receiverName) ||
      (sanitizePartyName(parsed.senderName) && !namesEqual(parsed.senderName, OUR_ACCOUNT_HOLDER)
        ? sanitizePartyName(parsed.senderName)
        : '') ||
      (parsedPerson && !namesEqual(parsedPerson, OUR_ACCOUNT_HOLDER) ? parsedPerson : '');
    receiverName = receiverCandidate;
  } else if (direction === 'in') {
    const senderCandidate =
      senderName ||
      sanitizePartyName(enriched.corrName) ||
      sanitizePartyName(parsed.senderName) ||
      (sanitizePartyName(parsed.receiverName) && !namesEqual(parsed.receiverName, OUR_ACCOUNT_HOLDER)
        ? sanitizePartyName(parsed.receiverName)
        : '') ||
      parsedPerson;
    senderName = senderCandidate;
    if (!receiverName) {
      receiverName = sanitizePartyName(enriched.ownName) || OUR_ACCOUNT_HOLDER;
    }
  } else if (!senderName && parsedPerson) {
    senderName = parsedPerson;
  }

  const corrIban = enriched.corrIban || '';
  const ownIban = normText(raw?.IBAN || raw?.iban || movement?.iban);

  let senderIban =
    movement?.senderIban ||
    pickRaw(raw, ['gonderenIban', 'senderIban']) ||
    parsed.senderIban ||
    '';
  let receiverIban =
    movement?.receiverIban ||
    pickRaw(raw, ['aliciIban', 'receiverIban', 'karsiIban', 'karsiHesapIban']) ||
    parsed.receiverIban ||
    '';

  if (direction === 'in') {
    if (!senderIban) senderIban = corrIban;
    if (!receiverIban) receiverIban = ownIban;
  } else if (direction === 'out') {
    if (!senderIban) senderIban = ownIban;
    if (!receiverIban) receiverIban = corrIban;
  } else if (!senderIban) {
    senderIban = corrIban || pickRaw(raw, ['corrIBAN', 'corrIban']);
  }

  return {
    bankName:
      movement?.bankLabel ||
      movement?.bankName ||
      pickRaw(raw, ['bankaAdi', 'bankName']) ||
      parsed.bankName ||
      '',
    senderName,
    receiverName,
    senderIban,
    receiverIban,
    senderAccount: movement?.senderAccount || enriched.corrAccount || senderIban || '',
    receiverAccount: movement?.receiverAccount || receiverIban || ''
  };
};

const normalizeOwnerCompareKey = (value) =>
  String(value || '')
    .trim()
    .toLocaleLowerCase('tr-TR')
    .replace(/[^a-zçğıöşü0-9\s]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const OWNER_NAME_TOKENS = normalizeOwnerCompareKey(OUR_ACCOUNT_HOLDER)
  .split(' ')
  .filter((t) => t.length >= 2);

/** Metin firma sahibi (Mehmet Demirsoy) adını taşıyor mu? */
export const mentionsOwnerAccountHolder = (value) => {
  const key = normalizeOwnerCompareKey(value);
  if (!key || OWNER_NAME_TOKENS.length < 2) return false;
  if (key === normalizeOwnerCompareKey(OUR_ACCOUNT_HOLDER)) return true;
  return OWNER_NAME_TOKENS.every((token) => key.includes(token));
};

const looksLikeInternalTransferText = (value) => {
  const text = String(value || '');
  if (!text.trim()) return false;
  if (/\bvirman\b/i.test(text)) return true;
  if (/cep\s*şube\s*[-–]?\s*hvl/i.test(text)) return true;
  if (/cep\s*sube\s*[-–]?\s*hvl/i.test(text)) return true;
  if (/\bcep\s*şube\b/i.test(text) && /\bhvl\b/i.test(text)) return true;
  if (/\bşubeler?\s*aras[ıi]\b/i.test(text)) return true;
  return false;
};

/**
 * Firma içi virman / kendi hesaplar arası hareket.
 * Gelen para da olsa gönderen+alıcı Mehmet Demirsoy ise fatura kesilmez.
 */
export const isInternalOwnerBankTransfer = (movement) => {
  if (!movement) return false;

  const parties = resolveMovementDetail(movement);
  const direction = String(movement?.direction || '').trim().toLowerCase();
  const sender = parties.senderName || '';
  const receiver =
    parties.receiverName ||
    (direction === 'in' || direction === '' ? OUR_ACCOUNT_HOLDER : '');

  const senderIsOwner = mentionsOwnerAccountHolder(sender);
  const receiverIsOwner = mentionsOwnerAccountHolder(receiver);

  if (senderIsOwner && receiverIsOwner) return true;

  // Firma hesapları arası virman — IBAN ve hesap etiketi üzerinden.
  // Eskiden yalnızca sahibin adına (ve "virman"/"cep şube-hvl" metnine) bakılıyordu;
  // açıklamasında isim geçmeyen bir hesaplar arası transfer yakalanmıyor ve
  // faturalandırılmaya çalışılıyordu. Bu kontrol yalnızca genişletir, daraltmaz.
  if (isOwnCompanyIban(parties.senderIban) && isOwnCompanyIban(parties.receiverIban)) {
    return true;
  }
  if (isOwnBankAccountPartyName(sender) && isOwnBankAccountPartyName(receiver)) {
    return true;
  }

  const blob = [
    movement.description,
    movement.reference,
    movement.senderName,
    movement.receiverName,
    movement.counterpartyName,
    parties.senderName,
    parties.receiverName,
    pickRaw(movement?.rawData || {}, [
      'explanation',
      'aciklama',
      'gonderenAdi',
      'aliciAdi',
      'customerName',
      'corrName'
    ])
  ]
    .filter(Boolean)
    .join(' ');

  // CEP ŞUBE-HVL / virman + sahip adı: yalnızca gönderen de bizsek iç transfer.
  // Gelen müşteri havalelerinde alıcı her zaman biziz; CEP ŞUBE-HVL açıklaması çok yaygın —
  // bunları iç virman sanmak fatura eşleşmesini düşürür (örn. Süleyman Keskin).
  if (looksLikeInternalTransferText(blob) && mentionsOwnerAccountHolder(blob)) {
    if (sender && !senderIsOwner) return false;
    return true;
  }

  return false;
};

export const getOurAccountHolderName = () => OUR_ACCOUNT_HOLDER;

export const resolveBankingIdForAppSube = (appSube) => resolveBankingId(appSube);
